import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { practiceComplianceAPI as API, SERVICE_OPTIONS, STRUCTURE_OPTIONS } from '../../lib/practiceComplianceAPI';

export const PMProfileTab = ({ onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [firmName, setFirmName] = useState('');
  const [f, setF] = useState({
    business_structure: 'company', principal_name: '', principal_title: 'Principal / Registered Tax Agent',
    services: ['tax_returns', 'bas_gst', 'bookkeeping', 'payroll', 'financial_statements', 'smsf_accounting', 'advisory'],
    staff_count: 1, softwareText: '', risk_appetite: '', asqm1_applicable: false, asqm1_attestation_note: '',
  });
  const [attestation, setAttestation] = useState(null);

  useEffect(() => {
    API.getProfile().then((res) => {
      const { profile, defaults, firm_name } = res.data;
      setFirmName(firm_name || '');
      if (profile) {
        setF((x) => ({
          ...x, ...profile,
          softwareText: (profile.software || defaults.software).join('\n'),
          risk_appetite: profile.risk_appetite || defaults.risk_appetite,
          asqm1_attestation_note: '',
        }));
        setAttestation(profile.asqm1_attestation || null);
      } else {
        setF((x) => ({ ...x, softwareText: defaults.software.join('\n'), risk_appetite: defaults.risk_appetite }));
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggleService = (key) => setF((x) => ({
    ...x, services: x.services.includes(key) ? x.services.filter((s) => s !== key) : [...x.services, key],
  }));

  const save = async () => {
    if (f.asqm1_applicable && !attestation && !f.asqm1_attestation_note.trim()) {
      toast.error('Enabling ASQM 1 requires an attestation note');
      return;
    }
    setSaving(true);
    try {
      const res = await API.saveProfile({
        business_structure: f.business_structure, principal_name: f.principal_name,
        principal_title: f.principal_title, services: f.services,
        staff_count: Number(f.staff_count) || 1,
        software: f.softwareText.split('\n').map((s) => s.trim()).filter(Boolean),
        risk_appetite: f.risk_appetite, asqm1_applicable: f.asqm1_applicable,
        asqm1_attestation_note: f.asqm1_attestation_note || null,
      });
      setAttestation(res.data?.asqm1_attestation || null);
      toast.success('Practice profile saved — regenerate manual drafts to apply changes');
      onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save profile'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="animate-pulse h-48 bg-muted rounded-lg mt-4" />;

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-lg">Practice Profile — {firmName}</CardTitle>
        <CardDescription>Drives manual generation: structure, services, staffing, software, risk appetite and ASQM 1 applicability.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label className="text-xs">Business structure</Label>
            <Select value={f.business_structure} onValueChange={(v) => setF((x) => ({ ...x, business_structure: v }))}>
              <SelectTrigger data-testid="pm-profile-structure"><SelectValue /></SelectTrigger>
              <SelectContent>{STRUCTURE_OPTIONS.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Principal name</Label><Input value={f.principal_name || ''} onChange={(e) => setF((x) => ({ ...x, principal_name: e.target.value }))} data-testid="pm-profile-principal" /></div>
          <div className="space-y-1"><Label className="text-xs">Principal title</Label><Input value={f.principal_title || ''} onChange={(e) => setF((x) => ({ ...x, principal_title: e.target.value }))} /></div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs">Services offered</Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {SERVICE_OPTIONS.map((s) => (
              <label key={s.key} className="flex items-center gap-2 border rounded-lg p-2 cursor-pointer hover:bg-muted/50 text-sm">
                <Checkbox checked={f.services.includes(s.key)} onCheckedChange={() => toggleService(s.key)} data-testid={`pm-svc-${s.key}`} />
                {s.label}
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-xs">Staff count (incl. principal)</Label>
            <Input type="number" min="1" value={f.staff_count} onChange={(e) => setF((x) => ({ ...x, staff_count: e.target.value }))} data-testid="pm-profile-staff" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Software stack (one per line)</Label>
            <Textarea rows={4} value={f.softwareText} onChange={(e) => setF((x) => ({ ...x, softwareText: e.target.value }))} data-testid="pm-profile-software" />
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Risk appetite statement</Label>
          <Textarea rows={3} value={f.risk_appetite} onChange={(e) => setF((x) => ({ ...x, risk_appetite: e.target.value }))} />
        </div>

        <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /> ASQM 1 applicability</p>
              <p className="text-xs text-muted-foreground">Only enable if the practice performs assurance engagements within ASQM 1 scope. Tax and bookkeeping alone do not trigger ASQM 1.</p>
            </div>
            <Switch checked={f.asqm1_applicable} onCheckedChange={(v) => setF((x) => ({ ...x, asqm1_applicable: v }))} data-testid="pm-asqm1-toggle" />
          </div>
          {f.asqm1_applicable && !attestation && (
            <div className="space-y-1">
              <Label className="text-xs">Attestation (required) — confirm in-scope services, qualifications and approved scope</Label>
              <Textarea rows={2} value={f.asqm1_attestation_note} onChange={(e) => setF((x) => ({ ...x, asqm1_attestation_note: e.target.value }))}
                placeholder="e.g. The practice performs SMSF audit engagements; the principal holds the required registrations…" data-testid="pm-asqm1-attestation" />
            </div>
          )}
          {f.asqm1_applicable && attestation && (
            <p className="text-xs text-green-700">Attested by {attestation.attested_by_name} on {new Date(attestation.attested_at).toLocaleDateString('en-AU')}: “{attestation.note}”</p>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="pm-profile-save">{saving ? 'Saving…' : 'Save Practice Profile'}</Button>
        </div>
      </CardContent>
    </Card>
  );
};
